import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

import {
  getParticipantsCount,
  getAccessCodesListExcel,
  getAllResultsInCombinedPDF,
  getAllResultsInSeparatePDFs,
  getUsedTANsList,
  deleteProject,
  updateProject,
  getStudentProfilesPDFZip,
  createParentVIPTestLink,
  changeProjectModerator,
  getSingleProject,
  getAccessCodesListPdf,
  getStudentsOfClass,
  deleteStudentFromClass,
  getVipTestPDF
} from '../../service';
import {
  downloadFile,
  combinePDFsFromURLs,
  downloadPDFsFromURLAsZip,
  fetchFileContent
} from '../../utils/download';

import { AdminContext } from '../../contexts/admin.context';

import AddParticipants from './AddParticipants';
import AccessCodeForm from '../AccessCodeForm';

import Spinner from '../common/Spinner';
import Button from '../common/Button';
import { DownloadButton } from '../common/DownloadButton';
import ClipboardCopyField from '../common/ClipboardCopyField';
import { DeleteIcon, PLusIconWithBorder, XIcon } from '../icons';
import { getTranslation } from '../../utils/translation';
import { formatDate } from '../../utils/date';
import { EyeIcon } from '@heroicons/react/24/outline';
import { POPUP_DEFAULT_VALUES } from '../../data/default-values';

const ProjectCard = ({
  project,
  serviceId,
  historyTab = false,
  classes,
  setClasses,
  moderators,
  parentsCustomVipTest = false,
  resources = null
}) => {
  const { t, i18n } = useTranslation();

  const {
    themeId,
    teamType,
    setContentPopupInfo,
    user,
    themeConfig: { primary, secondary, buttonRadius } = {},
    portalConfig: {
      classDownloadsConfig,
      projectTypes,
      parentsVIPProductId,
      allOtherProjectTypesCombined
    } = {}
  } = useContext(AdminContext);

  const [buttonLoadingStates, setButtonLoadingStates] = useState({
    projectType: false,
    allAccessCodes: false,
    allAccessCodesPdf: false,
    usedAccessCodes: false,
    vipOne: false,
    vipSeparate: false,
    strengthOne: false,
    strengthSeparate: false,
    rolesOne: false,
    rolesSeparate: false,
    studentOne: false,
    studentSeparate: false
  });
  const [participantsCount, setParticipantsCount] = useState(0);
  const [downloadingParticipants, setDownloadingParticipants] = useState(true);
  const [vipUrl, setVipUrl] = useState('');
  const [classWebpageLink, setClassWebpageLink] = useState('');
  const [downloadsConfig, setDownloadsConfig] = useState({});
  const [enableStudentPDFDownloads, setEnableStudentPDFDownloads] = useState({
    type: 'one',
    tans: [],
    pages: ['1 - 10']
  });
  const [parsedModerators, setParsedModerators] = useState([]);
  const [finalProjectResources, setFinalProjectResources] = useState(null);
  const [projectFileDownloadProgress, setProjectFileDownloadProgress] = useState({});
  const [studentsList, setStudentsList] = useState('loading');
  const [vimeoAndPdfDownloadProgress, setVimeoAndPdfDownloadProgress] = useState(0);

  useEffect(() => {
    if (resources?.projectFiles?.length > 0) {
      setFinalProjectResources(resources);
      return;
    }
    if (!resources?.projectFiles?.length) {
      const projectTypeObject = allOtherProjectTypesCombined?.find(
        (p) => p.id === project?.specs?.projectType
      );
      if (projectTypeObject) {
        setFinalProjectResources(projectTypeObject?.resources || []);
      }
    }
  }, [resources, allOtherProjectTypesCombined]);

  // Handle access codes download
  const handleDownloadsAccessCodes = async (type) => {
    const loadingKey =
      type === 'pdf' ? 'allAccessCodesPdf' : type === 'all' ? 'allAccessCodes' : 'usedAccessCodes';

    try {
      setButtonLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));

      const { id: pid, accountId: aid } = project;
      const response =
        type === 'pdf'
          ? await getAccessCodesListPdf({
              pid,
              aid,
              lang: i18n.language
            })
          : await getAccessCodesListExcel({
              pid,
              aid,
              lang: i18n.language
            });

      const downloadFileNamePrefix = `${project?.specs?.description || 'Codes'} - `;

      if (response.status === 200) {
        const fileName =
          downloadFileNamePrefix +
          (type === 'pdf'
            ? 'AccessCodes.pdf'
            : type === 'all'
            ? 'AccessCodes.xlsx'
            : 'UsedAccessCodes.xlsx');

        downloadFile(response.data, fileName);
        return;
      }
      toast.error(t('errors.failed-download-code'));
    } catch (e) {
      toast.error(t('errors.failed-download-code'));
      console.error('Failed to download access codes:', e);
    } finally {
      setButtonLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  /**
   * Download profiles and handle loading states and error messaging.
   *
   * @param {String} loadingKey - The key to identify loading states.
   * @param {String} fileSuffix - The file extension for the downloaded file.
   * @param {Function} downloadFunction - The function to download the file.
   * @param {Object} additionalParams - Additional parameters for the download function.
   */
  const handleProfileDownload = async (
    loadingKey,
    fileSuffix,
    downloadFunction,
    additionalParams = {}
  ) => {
    const { id: pid, accountId: aid } = project;
    const { tpl } = additionalParams; // Extract tpl from additionalParams

    try {
      // Set the loading state for the button
      setButtonLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));

      // Download the file
      // Pass only tpl from additionalParams if it exists
      const response = await downloadFunction({
        aid,
        pid,
        ...(tpl && { tpl })
      });

      // If successful, download the file to the client
      if (response.status === 200) {
        downloadFile(response.data, `${additionalParams.title}.${fileSuffix}`);
      }
    } catch (error) {
      // Display a generic error message to the user
      toast.error(getTranslation(t, 'error.failedToDownload') ?? `Failed to download`);

      // Log the specific error for debugging purposes
      console.error(`Failed to download due to: ${error.message || 'unknown error'}`);
    } finally {
      // Reset the loading state for the button
      setButtonLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  /**
   * Handle the download of various profile types.
   *
   * @param {String} profileType - The type of profile to download.
   * @param {String} downloadType - The type of download, either 'one' or not 'one'.
   */
  const handleProfilesDownload = async (profileType, downloadType) => {
    // Warn and return if the profileType is unknown
    if (!downloadsConfig[profileType]) {
      console.warn(`Unknown profileType: ${profileType}`);
      return;
    }

    const isDownloadTypeOne = downloadType === 'one';
    const loadingKey = `${profileType}${isDownloadTypeOne ? 'One' : 'Separate'}`;
    const fileSuffix = isDownloadTypeOne ? 'pdf' : 'zip';
    const downloadFunction = isDownloadTypeOne
      ? getAllResultsInCombinedPDF
      : getAllResultsInSeparatePDFs;

    handleProfileDownload(loadingKey, fileSuffix, downloadFunction, {
      ...downloadsConfig[profileType]
    });
  };

  // Handle student profile download
  const handleStudentProfilePDFDownload = async (type) => {
    // set loader
    const loadingKey = type === 'one' ? 'studentOne' : 'studentSeparate';
    setButtonLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));
    setEnableStudentPDFDownloads({ type, tans: [], pages: ['1 - 10'] });

    try {
      const { id: pid } = project;
      // get all used tans
      const { data: usedTansList } = await getUsedTANsList(pid);

      if (!usedTansList || !usedTansList.length) {
        toast.info("Aucun TAN utilisé n'a été trouvé");
        return;
      }

      const downloadPages = Math.ceil(usedTansList.length / 10);

      setEnableStudentPDFDownloads({
        type,
        tans: usedTansList,
        pages: Array.from({ length: downloadPages }, (_, i) => `${i * 10 + 1} - ${i * 10 + 10}`)
      });
    } catch (error) {
      toast.error('Échec du téléchargement du profil pdf');
      console.error('Profile Downloading Error: ', error);
    } finally {
      setButtonLoadingStates((prev) => ({
        ...prev,
        [loadingKey]: false
      }));
    }
  };

  // Download blocks of PDFs
  const downloadBlocksOfPDFs = async (loadingKey, page) => {
    try {
      setButtonLoadingStates((prev) => ({
        ...prev,
        [loadingKey]: true
      }));

      const { id: pid } = project;
      const { type, tans } = enableStudentPDFDownloads;

      const usedTansList = tans.slice(page * 10, (page + 1) * 10);

      const profilePDFURLs = [];

      // Prepare pdf urls
      for (const { tan, accountId, projectId } of usedTansList) {
        const userProfileId = `${themeId?.toLowerCase()?.trim()}/${teamType
          ?.toLowerCase()
          ?.trim()}/${accountId}/${projectId}/student-pdf/${tan}`;

        let pdfPageUrl = `${
          process.env.REACT_APP_MF_SITE_DOMAIN || 'https://www.mission-future.com'
        }/${userProfileId}`;

        if (project?.specs?.projectType !== 'default') pdfPageUrl += '?withProfessionMap=true';

        profilePDFURLs.push({ url: pdfPageUrl, id: userProfileId.split('/').join('-') });
      }

      const { data: { URL } = {} } = await getStudentProfilesPDFZip({
        studentProfiles: profilePDFURLs
      });

      let zipURL = URL;

      try {
        zipURL = JSON.parse(URL);
      } catch (e) {
        console.log('not json', e);
      }

      console.log('Zip URL', zipURL);

      const updateDownloadPercentage = (percentCompleted) => {
        setButtonLoadingStates((prev) => ({
          ...prev,
          [loadingKey]: percentCompleted
        }));
      };

      if (type === 'one') {
        await combinePDFsFromURLs(
          zipURL,
          `Combined-Student-Profiles-${pid}-${page + 1}-${(page + 1) * 10}`,
          updateDownloadPercentage
        );
      } else {
        await downloadPDFsFromURLAsZip(
          zipURL,
          `Student-Profiles-Archive-${pid}-${page}`,
          updateDownloadPercentage
        );
      }
    } catch (error) {
      toast.error('Échec du téléchargement du profil pdf');
      console.error('Profile Downloading Error: ', error);
    } finally {
      setButtonLoadingStates((prev) => ({
        ...prev,
        [loadingKey]: false
      }));
    }
  };

  const handleParticipantAdd = (newParticipantsCount) => {
    const updatedParticipantsCount = parseInt(participantsCount) + parseInt(newParticipantsCount);
    setParticipantsCount(updatedParticipantsCount);
  };

  const showAddParticipantPopup = () => {
    setContentPopupInfo({
      useLeftArrow: true,
      isVisible: true,
      content: (
        <AddParticipants project={project} projectId={project.id} onAdd={handleParticipantAdd} />
      )
    });
  };

  const showAccessCodeForm = () => {
    setContentPopupInfo({
      useLeftArrow: true,
      isVisible: true,
      content: (
        <AccessCodeForm projectId={project.id} accessCodeTpl={downloadsConfig?.accessCodes} />
      )
    });
  };

  // Get class details
  useEffect(() => {
    if (!project) return;
    const fetchParticipantsCount = async () => {
      try {
        if (serviceId === 'participants') {
          const response = await getParticipantsCount(project.id);
          const { count } = response.data;
          setParticipantsCount(count);
        }

        // Class webpage link
        if (project) {
          const { id: projectId, accountId } = project;
          const link = `${
            process.env.REACT_APP_MF_SITE_DOMAIN || 'https://www.mission-future.com'
          }/${themeId}/${teamType}/${accountId}/${projectId}`;
          setClassWebpageLink(link);
        }

        // Set downloads config
        if (classDownloadsConfig) {
          setDownloadsConfig({
            vip: {
              title: classDownloadsConfig?.profilePdfDownloadsConfigVipTitle
            },
            strength: {
              title: classDownloadsConfig?.profilePdfDownloadsConfigStrengthTitle,
              tpl: classDownloadsConfig?.profilePdfDownloadsConfigStrengthTpl
            },
            roles: {
              title: classDownloadsConfig?.profilePdfDownloadsConfigRolesTitle,
              tpl: classDownloadsConfig?.profilePdfDownloadsConfigRolesTpl
            },
            accessCodes: classDownloadsConfig?.pdfTemplateNamesAccessCodes
          });
        }
      } catch (error) {
        console.error('ERROR', error);
        toast.error(t('errors.failed-load-class-details'));
      } finally {
        setDownloadingParticipants(false);
      }
    };

    fetchParticipantsCount();
  }, [project]);

  useEffect(() => {
    // VIP URL
    if (project?.specs?.url || project?.specs?.vipTestUrl) {
      setVipUrl(project.specs.vipTestUrl || project.specs.url + `?lang=${i18n.language}`);
    }
  }, [project, i18n.language]);

  useEffect(() => {
    if (serviceId !== 'moderator') return;
    if (moderators?.length > 0) {
      const existingModerator = moderators.find(
        (moderator) => moderator.email === project?.specs?.moderator
      );
      if (existingModerator) {
        setParsedModerators(moderators);
      } else {
        setParsedModerators([
          {
            email: user?.id,
            firstName: user?.firstname || '',
            lastName: user?.lastname || '',
            accountOwner: true
          },
          ...moderators
        ]);
      }
    } else {
      setParsedModerators([
        {
          email: user?.id,
          firstName: user?.firstname || '',
          lastName: user?.lastname || '',
          accountOwner: true
        }
      ]);
    }
  }, [moderators, project?.specs?.moderator]);

  useEffect(() => {
    if (serviceId !== 'studentsListWithDeleteOption' || studentsList !== 'loading') return;

    (async () => {
      try {
        const { data } = await getStudentsOfClass(themeId, teamType, project.id, project.accountId);

        const parsedStudentList = data?.data
          ?.map((student) => ({
            ...student,
            name:
              student?.lastName && student?.firstName
                ? `${student?.lastName?.toUpperCase()} ${student?.firstName}`
                : student?.lastName || student.firstName || ''
          }))
          .sort((a, b) => a.name.localeCompare(b.name, i18n.language));

        setStudentsList(parsedStudentList || []);
      } catch (error) {
        console.error('ERROR', error);
        setStudentsList([]);
      }
    })();
  }, [serviceId, studentsList, project.id, project.accountId]);

  // console.log('Project Details ---------------');
  // console.log('Name', project.specs.description);
  // console.log('Created', project.created);
  // console.log('Participants', participantsCount); // Update button action: showAddParticipantPopup
  // console.log('VIP URL', vipUrl); // Update button action: showAccessCodeForm
  // console.log('Class webpage link', classWebpageLink);
  // console.log('Downloads config', downloadsConfig);
  // console.log('-----------------------------');

  return (
    <>
      {serviceId === 'created' && <p className="text-base">{formatDate(project.created)}</p>}

      {serviceId === 'participants' && (
        <>
          {!downloadingParticipants && (
            <div className="flex gap-2 items-center">
              <p className="text-base py-2 bg-[#F6F5F5] rounded-full text-[#606060] pl-4 w-16">
                {participantsCount}
              </p>
              <button
                className="flex items-center justify-center bg-primary text-white rounded-full w-8 h-8"
                onClick={showAddParticipantPopup}>
                <PLusIconWithBorder className="w-4 h-4" />
              </button>
            </div>
          )}
          {downloadingParticipants && <Spinner />}
        </>
      )}

      {serviceId === 'downloadAccessCodes' && (
        <DownloadButton
          onClick={() => handleDownloadsAccessCodes('all')}
          isLoading={buttonLoadingStates.allAccessCodes}>
          <p className="text-base">{getTranslation(t, `class.table.${serviceId}.btn`)}</p>
        </DownloadButton>
      )}

      {serviceId === 'downloadAccessCodesPdf' && (
        <DownloadButton
          onClick={() => handleDownloadsAccessCodes('pdf')}
          isLoading={buttonLoadingStates.allAccessCodesPdf}>
          <p className="text-base">{getTranslation(t, `class.table.${serviceId}.btn`)}</p>
        </DownloadButton>
      )}

      {serviceId === 'downloadUsedAccessCodes' && (
        <DownloadButton
          onClick={() => handleDownloadsAccessCodes('used')}
          isLoading={buttonLoadingStates.usedAccessCodes}>
          <p className="text-base">{getTranslation(t, `class.table.${serviceId}.btn`)}</p>
        </DownloadButton>
      )}

      {serviceId === 'classWebpage' && <ClipboardCopyField textToCopy={classWebpageLink} />}

      {serviceId === 'vipTestLink' && (
        <StudentVIPTestLink
          vipUrl={vipUrl}
          project={project}
          showCopyField={true}
          showDownloadButton={false}
          lang={i18n.language}
        />
      )}

      {serviceId === 'parentVipTestLink' && parentsVIPProductId && (
        <ParentVIPTestLink
          t={t}
          themeId={themeId}
          teamType={teamType}
          serviceId={serviceId}
          productId={parentsVIPProductId}
          project={project}
          classes={classes}
          setClasses={setClasses}
          parentsCustomVipTest={parentsCustomVipTest}
          lang={i18n.language}
        />
      )}

      {serviceId === 'downloadVipProfiles' && (
        <div className="flex gap-2">
          <DownloadButton
            onClick={() => handleProfilesDownload('vip', 'one')}
            isLoading={buttonLoadingStates.vipOne}
            buttonType={'primary'}>
            {getTranslation(t, `class.table.${serviceId}.btn.one`)}
          </DownloadButton>
          <DownloadButton
            onClick={() => handleProfilesDownload('vip', 'separate')}
            isLoading={buttonLoadingStates.vipSeparate}
            buttonType={'outline'}>
            {getTranslation(t, `class.table.${serviceId}.btn.separate`)}
          </DownloadButton>
        </div>
      )}

      {serviceId === 'downloadStrengthProfiles' && (
        <div className="flex gap-2">
          <DownloadButton
            onClick={() => handleProfilesDownload('strength', 'one')}
            isLoading={buttonLoadingStates.strengthOne}
            buttonType={'primary'}>
            {getTranslation(t, `class.table.${serviceId}.btn.one`)}
          </DownloadButton>
          <DownloadButton
            onClick={() => handleProfilesDownload('strength', 'separate')}
            isLoading={buttonLoadingStates.strengthSeparate}
            buttonType={'outline'}>
            {getTranslation(t, `class.table.${serviceId}.btn.separate`)}
          </DownloadButton>
        </div>
      )}

      {serviceId === 'downloadProfessionalRoleProfiles' && (
        <div className="flex gap-2">
          <DownloadButton
            onClick={() => handleProfilesDownload('roles', 'one')}
            isLoading={buttonLoadingStates.rolesOne}
            buttonType={'primary'}>
            {getTranslation(t, `class.table.${serviceId}.btn.one`)}
          </DownloadButton>
          <DownloadButton
            onClick={() => handleProfilesDownload('roles', 'separate')}
            isLoading={buttonLoadingStates.rolesSeparate}
            buttonType={'outline'}>
            {getTranslation(t, `class.table.${serviceId}.btn.separate`)}
          </DownloadButton>
        </div>
      )}

      {serviceId === 'downloadStudentProfiles' && (
        <>
          <div className="flex gap-2">
            <DownloadButton
              onClick={() => handleStudentProfilePDFDownload('one')}
              isLoading={buttonLoadingStates.studentOne}
              buttonType={'primary'}>
              {getTranslation(t, `class.table.${serviceId}.btn.one`)}
            </DownloadButton>
            <DownloadButton
              onClick={() => handleStudentProfilePDFDownload('separate')}
              isLoading={buttonLoadingStates.studentSeparate}
              buttonType={'outline'}>
              {getTranslation(t, `class.table.${serviceId}.btn.separate`)}
            </DownloadButton>
          </div>
          {enableStudentPDFDownloads.tans.length > 0 && (
            <div className="flex gap-2 mt-2 w-full">
              {enableStudentPDFDownloads?.pages.map((page, index) => (
                <DownloadButton
                  key={index}
                  onClick={() => downloadBlocksOfPDFs(`studentPFDownloadBlock-${page}`, index)}
                  isLoading={buttonLoadingStates?.[`studentPFDownloadBlock-${page}`]}
                  buttonType={'outline'}>
                  {page}
                </DownloadButton>
              ))}
            </div>
          )}
        </>
      )}

      {serviceId === 'profileConverter' && (
        <Button onClick={() => showAccessCodeForm()} buttonType={'primary'}>
          {getTranslation(t, `class.table.${serviceId}.btn.one`)}
        </Button>
      )}

      {serviceId === 'delete' && (
        <span
          className="flex w-full justify-center cursor-pointer"
          onClick={() => {
            Swal.fire({
              title: getTranslation(t, `class.table.${serviceId}.popup.title`),
              text: getTranslation(t, `class.table.${serviceId}.popup.text`),
              icon: 'warning',
              showCancelButton: true,
              confirmButtonText: getTranslation(t, `class.table.${serviceId}.popup.confirm`),
              cancelButtonText: getTranslation(t, `class.table.${serviceId}.popup.cancel`),
              iconColor: primary,
              confirmButtonColor: primary,
              reverseButtons: true,
              didOpen: () => {
                const confirmButton = Swal.getConfirmButton();
                const cancelButton = Swal.getCancelButton();
                // Set the border-radius directly
                if (confirmButton) confirmButton.style.borderRadius = buttonRadius;
                if (cancelButton) {
                  cancelButton.style.backgroundColor = 'transparent';
                  cancelButton.style.color = primary;
                  cancelButton.style.border = `1px solid ${primary}`;
                  cancelButton.style.borderRadius = buttonRadius;
                }
              }
            }).then(async (result) => {
              if (result.isConfirmed) {
                try {
                  await deleteProject(project.id);

                  setClasses(classes.filter((classItem) => classItem.id !== project.id));

                  toast.success(getTranslation(t, `class.table.${serviceId}.success`));
                } catch (error) {
                  console.log(error);
                  toast.error(getTranslation(t, `class.table.${serviceId}.fail`));
                }
              }
            });
          }}>
          <Button buttonType={'customBG'}>
            <DeleteIcon className="w-4 h-4" />
          </Button>
        </span>
      )}

      {serviceId === 'projectType' && (
        <>
          {buttonLoadingStates?.projectType ? (
            <Spinner />
          ) : (
            <select
              className={`px-2 py-1.5 rounded text-gray-primary text-sm bg-gray-100 w-48`}
              style={{ borderRadius: buttonRadius }}
              id="projectType"
              name="projectType"
              defaultValue={project?.specs?.projectType || projectTypes?.[0]?.id}
              onChange={async (e) => {
                try {
                  if (historyTab) return;

                  setButtonLoadingStates({ ...buttonLoadingStates, projectType: true });

                  const updatedValue = e.target.value;
                  const teacherRegion = user?.address?.[0]?.street;
                  const updatedProject = {
                    ...project,
                    specs: {
                      ...project.specs,
                      projectType: updatedValue,
                      teamType: teamType,
                      teacherRegion
                    }
                  };

                  const projectValues = {
                    pid: updatedProject.id,
                    specs: {
                      ...updatedProject.specs
                    }
                  };

                  await updateProject(projectValues);

                  const updatedClasses = classes.map((classItem) => {
                    if (classItem.id === updatedProject.id) {
                      return updatedProject;
                    }
                    return classItem;
                  });

                  setClasses(updatedClasses);
                } catch (error) {
                  toast.error('Échec de la mise à jour du type de projet');
                } finally {
                  setButtonLoadingStates({ ...buttonLoadingStates, projectType: false });
                }
              }}>
              {projectTypes?.length > 0 &&
                projectTypes?.map(({ id, title, enabled }, index) => (
                  <option key={index} value={id} disabled={!enabled}>
                    {title?.[i18n.language] || title?.en || title?.de || title?.fr || title?.it}
                  </option>
                ))}
            </select>
          )}
        </>
      )}

      {serviceId === 'moderator' && (
        <>
          {buttonLoadingStates?.moderator ? (
            <Spinner />
          ) : (
            <select
              className={`px-2 py-1.5 rounded text-gray-primary text-sm bg-gray-100 w-64`}
              style={{ borderRadius: buttonRadius }}
              id="moderator"
              name="moderator"
              value={
                parsedModerators?.find((moderator) => moderator.email === project?.specs?.moderator)
                  ? project?.specs?.moderator
                  : user?.id
              }
              onChange={async (e) => {
                try {
                  if (historyTab) return;

                  setButtonLoadingStates({ ...buttonLoadingStates, moderator: true });

                  const updatedValue = e.target.value;
                  const teacherRegion = user?.address?.[0]?.street;
                  const updatedProject = {
                    ...project,
                    specs: {
                      ...project.specs,
                      teacherRegion,
                      moderator: updatedValue
                    }
                  };

                  await changeProjectModerator({
                    projectId: updatedProject.id,
                    moderatorEmail: updatedValue
                  });

                  const updatedClasses = classes.map((classItem) => {
                    if (classItem.id === updatedProject.id) {
                      return updatedProject;
                    }
                    return classItem;
                  });

                  setClasses(updatedClasses);
                } catch (error) {
                  toast.error('Échec de la mise à jour du modérateur');
                } finally {
                  setButtonLoadingStates({ ...buttonLoadingStates, moderator: false });
                }
              }}>
              {parsedModerators?.length > 0 &&
                parsedModerators?.map(({ email, firstName, lastName, accountOwner }, index) => (
                  <option key={index} value={email} disabled={accountOwner}>
                    {firstName} {lastName} ({email})
                  </option>
                ))}
            </select>
          )}
        </>
      )}

      {serviceId === 'projectFiles' && finalProjectResources?.projectFiles?.length > 0 && (
        <div className="flex gap-2">
          {finalProjectResources?.projectFiles?.map((projectFile, index) => {
            const { title, url, type, colors } = projectFile;

            const finalTitle =
              title?.[i18n.language] ||
              title?.en ||
              title?.de ||
              title?.fr ||
              title?.it ||
              getTranslation(t, `class.table.${serviceId}.downloadFile`);

            const finalUrl = url?.[i18n.language]
              ? url?.[i18n.language]
              : typeof url === 'string'
              ? url
              : '';

            if (!finalUrl) return null;

            return (
              <DownloadButton
                key={index}
                bg={colors?.background || '#000'}
                color={colors?.text || '#fff'}
                buttonType={'customBG'}
                onClick={async () => {
                  try {
                    setProjectFileDownloadProgress((prev) => ({
                      ...prev,
                      [`${project?.specs?.projectType}-${index}`]: 0.1
                    }));

                    const fileContent = await fetchFileContent(
                      process.env.REACT_APP_AWS_URL + finalUrl,
                      (progress) =>
                        setProjectFileDownloadProgress((prev) => ({
                          ...prev,
                          [`${project?.specs?.projectType}-${index}`]: progress
                        }))
                    );

                    downloadFile(fileContent, finalTitle);
                  } catch (error) {
                    toast.error(t('errors.failedDownloadFile'));
                  } finally {
                    setProjectFileDownloadProgress((prev) => ({
                      ...prev,
                      [`${project?.specs?.projectType}-${index}`]: false
                    }));
                  }
                }}
                isLoading={
                  projectFileDownloadProgress?.[`${project?.specs?.projectType}-${index}`]
                }>
                <span className="font-normal" dangerouslySetInnerHTML={{ __html: finalTitle }} />
              </DownloadButton>
            );
          })}
        </div>
      )}

      {serviceId === 'studentsListWithDeleteOption' && studentsList !== 'loading' && (
        <div className="flex gap-12">
          <div className="flex gap-2 items-center justify-center">
            <span className="bg-gray-100 rounded-xl px-8 py-2 text-black text-md">
              {studentsList?.length}
            </span>
            <span
              className="text-lg text-md text-black"
              dangerouslySetInnerHTML={{
                __html: getTranslation(t, `studentsListWithDeleteOption.students`)
              }}
            />
          </div>

          <button
            className="bg-black rounded-full w-10 h-10 flex items-center justify-center cursor-pointer hover:scale-105 transition-all duration-300 active:scale-95"
            onClick={() => {
              setContentPopupInfo({
                useLeftArrow: true,
                isVisible: true,
                maxWidth: 'max-w-lg',
                onHide: () => setContentPopupInfo(POPUP_DEFAULT_VALUES),
                title: getTranslation(t, 'studentsListWithDeleteOption.listPopup.title'),
                content: (
                  <div className="flex flex-col w-full border-black/30 bg-[#FAFAFA] rounded-lg p-2 my-4">
                    {studentsList.map((student, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between gap-10 py-2.5"
                        style={{
                          borderBottom:
                            index !== studentsList.length - 1 ? '1px solid #515151' : 'none'
                        }}>
                        <span className="min-w-0 break-words text-lg text-[#454444]">
                          <span className="font-bold">{index + 1}.</span> {student?.name}
                          {student?.alias?.trim() && (
                            <span className="text-blue-primary"> « {student.alias.trim()} »</span>
                          )}
                        </span>
                        <button
                          key={index}
                          className="w-8 h-8 shrink-0 p-2 bg-[#444444] flex items-center justify-center transition-all duration-300 hover:bg-black rounded-full cursor-pointer"
                          onClick={async () => {
                            Swal.fire({
                              title:
                                getTranslation(t, `studentsListWithDeleteOption.popup.title`) +
                                ' ' +
                                "'" +
                                student?.name +
                                "'",
                              text: getTranslation(t, `studentsListWithDeleteOption.popup.text`),
                              icon: 'warning',
                              showCancelButton: true,
                              confirmButtonText: getTranslation(
                                t,
                                `studentsListWithDeleteOption.popup.confirm`
                              ),
                              cancelButtonText: getTranslation(
                                t,
                                `studentsListWithDeleteOption.popup.cancel`
                              ),
                              iconColor: primary,
                              confirmButtonColor: primary,
                              reverseButtons: true,
                              didOpen: () => {
                                const confirmButton = Swal.getConfirmButton();
                                const cancelButton = Swal.getCancelButton();
                                // Set the border-radius directly
                                if (confirmButton) confirmButton.style.borderRadius = buttonRadius;
                                if (cancelButton) {
                                  cancelButton.style.backgroundColor = 'transparent';
                                  cancelButton.style.color = primary;
                                  cancelButton.style.border = `1px solid ${primary}`;
                                  cancelButton.style.borderRadius = buttonRadius;
                                }
                              }
                            }).then(async (result) => {
                              if (result.isConfirmed) {
                                try {
                                  await deleteStudentFromClass(student._id);

                                  setStudentsList(
                                    studentsList.filter((s) => s._id !== student._id)
                                  );

                                  toast.success(
                                    getTranslation(t, `studentsListWithDeleteOption.success`)
                                  );
                                } catch (error) {
                                  console.log(error);
                                  toast.error(
                                    getTranslation(t, `studentsListWithDeleteOption.fail`)
                                  );
                                }
                              }
                            });
                          }}>
                          <XIcon className="w-full h-full" color={'#fff'} />
                        </button>
                      </div>
                    ))}
                  </div>
                )
              });
            }}>
            <EyeIcon className="w-6 text-white" />
          </button>
        </div>
      )}

      {serviceId === 'studentsListWithDeleteOption' && studentsList === 'loading' && (
        <div className="flex justify-center items-center h-full">
          <Spinner />
        </div>
      )}

      {serviceId === 'vimeoAndPdfDownload' && (
        <div className="flex gap-3 items-center justify-center">
          {finalProjectResources?.vimeoAndPdfDownload?.vimeoLink?.[i18n.language] && (
            <ClipboardCopyField
              textToCopy={finalProjectResources?.vimeoAndPdfDownload?.vimeoLink?.[i18n.language]}
              showCopyIcon={false}
            />
          )}
          {!finalProjectResources?.vimeoAndPdfDownload?.studentVipPdfDownload &&
            finalProjectResources?.vimeoAndPdfDownload?.pdfLink?.[i18n.language] && (
              <DownloadButton
                buttonType={'customBG'}
                bg={'#515151'}
                color={'#fff'}
                extraClass={'!rounded-full w-9 h-9 flex items-center justify-center shadow'}
                downloadIconClass={'!w-5 !h-5'}
                isLoading={vimeoAndPdfDownloadProgress}
                onClick={async () => {
                  try {
                    const pdfDownloadLink =
                      finalProjectResources?.vimeoAndPdfDownload?.pdfLink?.[i18n.language];

                    if (!pdfDownloadLink) {
                      toast.error(t('errors.failedDownloadFile'));
                      return;
                    }

                    const pdfContent = await fetchFileContent(
                      process.env.REACT_APP_AWS_URL + pdfDownloadLink,
                      (progress) => setVimeoAndPdfDownloadProgress(progress)
                    );

                    downloadFile(pdfContent, 'PDF Download');
                  } catch (error) {
                    toast.error(t('errors.failedDownloadFile'));
                  } finally {
                    setVimeoAndPdfDownloadProgress(0);
                  }
                }}
              />
            )}
          {finalProjectResources?.vimeoAndPdfDownload?.studentVipPdfDownload && (
            <StudentVIPTestLink
              vipUrl={vipUrl}
              project={project}
              showCopyField={false}
              showDownloadButton={true}
              lang={i18n.language}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ProjectCard;

const StudentVIPTestLink = ({
  vipUrl,
  project,
  showCopyField = true,
  showDownloadButton = false,
  lang
}) => {
  const [loading, setLoading] = useState(false);

  const handleStudentVipTestPDFDownload = async () => {
    try {
      setLoading(true);
      const response = await getVipTestPDF({
        aid: project?.accountId,
        pid: project?.id,
        type: 1,
        lang: project?.specs?.language || lang
      });
      downloadFile(response.data, 'mission future VIP.pdf');
    } catch (error) {
      console.error('Failed to download student VIP test PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex gap-2 items-center justify-center">
      {showCopyField && <ClipboardCopyField textToCopy={vipUrl} />}

      {showDownloadButton && (
        <DownloadButton
          onClick={handleStudentVipTestPDFDownload}
          isLoading={loading}
          buttonType={'customBG'}
          bg={'#515151'}
          color={'#fff'}
          extraClass={'!rounded-full w-9 h-9 flex items-center justify-center shadow'}
          downloadIconClass={'!w-5 !h-5'}
        />
      )}
    </div>
  );
};

const ParentVIPTestLink = ({
  t,
  themeId,
  teamType,
  serviceId,
  productId,
  project,
  classes,
  setClasses,
  parentsCustomVipTest,
  lang
}) => {
  const [parentProjectId, setParentProjectId] = useState(0);
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchLink = async () => {
    try {
      setLoading(true);
      const response = await createParentVIPTestLink(productId, project?.id);

      const parentVipLink = parentsCustomVipTest
        ? `${
            process.env.REACT_APP_MF_SITE_DOMAIN || 'https://www.mission-future.com'
          }/${themeId}/${teamType}/${project?.accountId}/${
            response?.data?.parentVIPProjectID
          }/vip/parent`
        : response.data.url;

      setLink(parentVipLink);

      // Store parent VIP link in specs of project
      const updatedProject = {
        ...project,
        specs: {
          ...project.specs,
          parentsCustomVipTest,
          parentVIPTestLink: parentVipLink,
          parentVIPProjectID: response.data?.parentVIPProjectID,
          parentVIPTestLinkValidUntil:
            response.data?.validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        }
      };

      const projectValues = {
        pid: updatedProject.id,
        specs: {
          ...updatedProject.specs
        }
      };

      await updateProject(projectValues);

      const updatedClasses = classes.map((classItem) => {
        if (classItem.id === updatedProject.id) {
          return updatedProject;
        }
        return classItem;
      });

      setClasses(updatedClasses);

      return response?.data?.parentVIPProjectID;
    } catch (error) {
      console.error('Failed to create parent VIP test link:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleParentVipTestPDFDownload = async () => {
    try {
      let parentProjectIdLocal = 0;
      if (!parentProjectId) parentProjectIdLocal = await fetchLink();

      setLoading(true);

      const response = await getVipTestPDF({
        aid: project?.accountId,
        pid: parentProjectId || parentProjectIdLocal,
        type: 2,
        lang: project?.specs?.language || lang // use project lang if available
      });
      downloadFile(response.data, 'mission future VIP.pdf');
    } catch (error) {
      console.error('Failed to download parent VIP test PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (
          project?.specs?.parentVIPTestLink &&
          new Date(project?.specs?.parentVIPTestLinkValidUntil) > new Date()
        ) {
          if (!project?.specs?.parentVIPProjectID) return;

          const { data: parentVIPProjectDetails } = await getSingleProject(
            project?.specs?.parentVIPProjectID
          );

          setParentProjectId(parentVIPProjectDetails?.id);

          const onlineStatus = parentVIPProjectDetails?.specs?.online;
          if (onlineStatus === false) return; // explicity checking false because undefined online variable means its online project

          const parentVipLink = parentsCustomVipTest
            ? `${
                process.env.REACT_APP_MF_SITE_DOMAIN || 'https://www.mission-future.com'
              }/${themeId}/${teamType}/${project?.accountId}/${
                project?.specs?.parentVIPProjectID
              }/vip/parent`
            : parentVIPProjectDetails?.specs?.url;

          setLink(parentVipLink);
        }
      } catch (error) {
        console.error('Failed to fetch parent VIP test link:', error);
      }
    })();
  }, [project, parentsCustomVipTest, themeId, teamType]);

  return (
    <div className="flex gap-2 items-center justify-center">
      {/* {!link && (
        <Button onClick={fetchLink} buttonType={'primary'} loading={loading}>
          <span
            dangerouslySetInnerHTML={{
              __html: getTranslation(t, `class.table.parentVIPTestLink.btn`)
            }}
          />
        </Button>
      )}
      {link && <ClipboardCopyField textToCopy={link + `?lang=${lang}`} />} */}

      <DownloadButton
        onClick={handleParentVipTestPDFDownload}
        isLoading={loading}
        buttonType={'customBG'}
        bg={'#515151'}
        color={'#fff'}
        extraClass={'!rounded-full w-9 h-9 flex items-center justify-center shadow'}
        downloadIconClass={'!w-5 !h-5'}
      />
    </div>
  );
};
